import { Injectable, inject } from '@angular/core';

import { AzureStorageService } from '~/services/azure-storage.service';
import { DocumentFinderApiService, HistoryRow, Pack, SearchSummary } from '~/services/document-finder-api.service';
import {
    Decision, DocType, FoundDocument, INPUT_TYPES, InputDocType, L2_PROCESSES, Landscape,
    LANDSCAPES, MissingDocument, PublishedPack, ReviewFilters, SearchScope, SortKey, SOURCE_KEYS, SOURCES, View,
    DOC_TYPES, CONNECTED_FOLDER_KEY, DEFAULT_QUERY, PROCESS_MAP, REASONS, SIGNALS, SUGGESTIONS, TAXONOMY, TRACE, applyDocTypeReference,
    confidenceClass, documentLandscape, gapKey, landscapeLabel, replaceAll, sourceName
} from '~/models/document-finder.models';

export const AUTO_INCLUDE_THRESHOLD = 70;
export const DEFAULT_PROCESS_CODES = ['DS-020', 'LSC-210', 'LSC-310', 'LSC-630', 'M-040'];
const ADDITIONAL_PROCESS_CODES = ['LLC-220', 'O-010', 'K-110'];

export interface GeneratedDeck { pptx: Blob; }

/**
 * Holds the state of the Document Discovery workflow (Ask → Retrieve → Review → Feedback → Publish)
 * and exposes the business rules used by the UI components. All data comes from the backend API.
 */
@Injectable({ providedIn: 'root' })
export class DocumentFinderService {
    /** Search results from POST api/document-finder/search. */
    public documents: FoundDocument[] = [];
    private api = inject(DocumentFinderApiService);
    public azure = inject(AzureStorageService);
    public deck: { prompt: string; original: string; busy: boolean; result: GeneratedDeck | null } =
        { prompt: '', original: '', busy: false, result: null };

    public view: View = 'search';
    public step: number = 1;
    public maxStep: number = 1;
    public collapsed: boolean = false;
    public running: boolean = false;

    public query: string = DEFAULT_QUERY;
    public scope: SearchScope = this.defaultScope();

    /** Reference data state. */
    public referenceLoaded: boolean = false;
    public referenceError: string = '';
    public user: string = '';
    public history: HistoryRow[] = [];
    public packs: Pack[] = [];
    /** Error from the last search, shown in the UI. */
    public searchError: string = '';
    public searchWarning = '';

    /** Long document-type labels from bootstrap.types (applied to DOC_TYPES with the taxonomy). */
    private docTypeLabels: Record<string, string> = {};

    public decisions: Record<string, Decision> = {};
    private approved = new Set<string>();
    public reasons: Record<string, string> = {};
    public whys: Record<string, string> = {};

    public tab: 'all' | 'x' | DocType = 'all';
    /** Documents decided in this review; they leave the queue (less to scan) until "Show decided again". */
    public done: Set<string> = new Set<string>();
    public filters: ReviewFilters = this.defaultFilters();
    public sort: SortKey = 'conf';

    public missing: MissingDocument[] = [];
    public notApplicable: Set<string> = new Set<string>();
    public selection: Set<string> = new Set<string>();
    public published: PublishedPack | null = null;
    /** PBL backlog decisions by id ('approved' | 'rejected'; absent = pending). Null until the PBL step seeds it. */
    public pbl: Record<string, string> | null = null;
    public pblKinds: Record<string, string> = {};

    // overlays
    public drawerDocId: string | null = null;
    public excludeIds: string[] | null = null;
    public missingModal: { open: boolean; key: string | null; index: number | null } = { open: false, key: null, index: null };
    public missingUploadBusy = false;
    public toastMessage: string = '';
    public toastVisible: boolean = false;
    private toastHandle: any;

    constructor() {
        this.resetDecisions();
        this.loadReference();
    }

    //*************************************************************************
    //  Reference data (bootstrap, process map, taxonomy, history, packs)
    //*************************************************************************
    /** Loads all reference data from the API and fills the shared containers. */
    public async loadReference(): Promise<void> {
        if (!this.api.enabled) {
            this.referenceError = 'API is not configured (assets/config.json → apiUrl).';
            return;
        }
        try {
            const b = await this.api.bootstrap();
            if (b) {
                this.user = b.user ?? '';
                if (b.signals?.length) { replaceAll(SIGNALS, b.signals); }
                if (b.suggestions?.length) { replaceAll(SUGGESTIONS, b.suggestions); }
                if (b.default_query) { this.query = b.default_query; }
                replaceAll(L2_PROCESSES, (b.l2 ?? []).map(x => ({ id: x.id, name: x.name })));
                [...DEFAULT_PROCESS_CODES, ...ADDITIONAL_PROCESS_CODES].forEach(id => {
                    if (!L2_PROCESSES.some(process => process.id === id)) {
                        L2_PROCESSES.push({ id, name: '' });
                    }
                });
                Object.keys(SOURCES).forEach(k => { if (k !== CONNECTED_FOLDER_KEY) { delete SOURCES[k]; } });
                (b.sources ?? []).forEach(s => SOURCES[s.key] = { name: s.name, n: s.n });
                replaceAll(SOURCE_KEYS, (b.sources ?? []).map(s => s.key));
                SOURCES['sg'] ??= { name: 'Signavio', n: null };
                if (!SOURCE_KEYS.includes('sg')) { SOURCE_KEYS.push('sg'); }
                replaceAll(REASONS, b.reasons ?? []);
                this.docTypeLabels = b.types ?? {};
                this.scope = this.defaultScope();
            }
            this.referenceLoaded = true;
            this.referenceError = '';
        } catch (err: any) {
            console.error('Loading reference data failed', err);
            this.referenceError = 'Could not load reference data from the API.';
        }
        await Promise.all([this.loadProcessMap(), this.loadTaxonomy(), this.loadHistory(), this.loadPacks(), this.loadSignavioCount()]);
    }

    public async loadSignavioCount(): Promise<void> {
        try {
            const result = await this.api.signavioCount();
            if (SOURCES['sg']) { SOURCES['sg'].n = result.total_documents; }
        } catch (err) { console.error('Loading Signavio index count failed', err); }
    }

    public async loadProcessMap(): Promise<void> {
        try { replaceAll(PROCESS_MAP, (await this.api.getProcessMap()) ?? []); }
        catch (err) { console.error('Loading process map failed', err); }
    }

    public async loadTaxonomy(): Promise<void> {
        try {
            const t = await this.api.getTaxonomy();
            replaceAll(TAXONOMY.rows, t?.rows ?? []);
            replaceAll(TAXONOMY.acronyms, t?.acronyms ?? []);
            applyDocTypeReference(this.docTypeLabels, TAXONOMY.rows);
        } catch (err) { console.error('Loading taxonomy failed', err); }
    }

    public async loadHistory(): Promise<void> {
        try { this.history = (await this.api.getHistory()) ?? []; }
        catch (err) { console.error('Loading history failed', err); }
    }

    public async loadPacks(): Promise<void> {
        try { this.packs = (await this.api.getPacks()) ?? []; }
        catch (err) { console.error('Loading packs failed', err); }
    }

    //*************************************************************************
    //  Scope / search
    //*************************************************************************
    public defaultScope(): SearchScope {
        return {
            l2: new Set(DEFAULT_PROCESS_CODES),
            land: 'both',
            types: new Set<InputDocType>(INPUT_TYPES),
            src: new Set(SOURCE_KEYS)
        };
    }

    public defaultFilters(): ReviewFilters {
        return { conf: 'all', l2: 'all', src: 'all', land: 'all', q: '' };
    }

    public resetDecisions(): void {
        this.decisions = {};
        this.approved.clear();
        this.documents.forEach(d => {
            this.decisions[d.id] = d.agent
                ? (d.agent === 'inc' ? 'inc' : d.agent === 'call' ? 'pend' : 'exc')
                : d.conf >= AUTO_INCLUDE_THRESHOLD && d.t !== 'other' ? 'inc' : 'pend';
        });
        this.reasons = {};
        this.whys = {};
        this.done = new Set<string>();
    }

    /** True when the agent itself excluded the document and the reviewer has not overruled it. */
    public agentExcluded(d: FoundDocument): boolean {
        return d.agent === 'exc' && this.decisions[d.id] === 'exc';
    }

    /** Reason shown for an irrelevant document: the reviewer's, else the agent's. */
    public reasonOf(d: FoundDocument): string | undefined {
        return this.reasons[d.id] || (d.agent === 'exc' ? d.agentReason : undefined);
    }

    public showDecidedAgain(): void {
        this.done = new Set<string>();
    }

    /** Toggle an item in a scope set, never allowing the set to become empty. */
    public toggleScope<K extends 'l2' | 'types' | 'src'>(set: K, key: string): void {
        const s = this.scope[set] as Set<string>;
        if (s.has(key)) {
            if (s.size > 1) {
                s.delete(key);
            }
        } else {
            s.add(key);
        }
    }

    public inScope(d: FoundDocument): boolean {
        const sc = this.scope;
        if (!sc.src.has(d.src)) { return false; }
        if (d.t !== 'other' && !sc.types.has(d.t)) { return false; }
        if (sc.land !== 'both' && this.coverageLandscape(d) !== sc.land && d.t !== 'other') { return false; }
        if (d.l2.length && !d.l2.some(x => sc.l2.has(x))) { return false; }
        return true;
    }

    public found(): FoundDocument[] {
        return this.documents.filter(d => this.inScope(d));
    }

    public count(decision: Decision): number {
        return this.found().filter(d => this.decisions[d.id] === decision && !(decision === 'exc' && d.agent === 'exc')).length;
    }

    public totalFilesInSources(): number {
        return [...this.scope.src].reduce((a, k) => a + (SOURCES[k]?.n ?? 0), 0);
    }

    //*************************************************************************
    //  Navigation
    //*************************************************************************
    public go(step: number): void {
        this.step = step;
        this.maxStep = Math.max(this.maxStep, step);
        this.view = 'search';
    }

    public navigate(view: View): void {
        this.view = view;
        if (view === 'search' && this.step === 2) {
            this.step = 3;
        }
    }

    public startSearch(query: string): void {
        this.query = query.trim() || this.query;
        this.documents = [];
        this.api.lastSearch = null;
        this.api.lastRunId = null;
        this.missing = [];
        this.resetDecisions();
        this.tab = 'all';
        this.running = true;
        this.go(2);
        this.loadDocuments();
    }

    /** Fetches the documents for the current query/scope from the search API. */
    public loadDocuments(): Promise<void> {
        if (this.azure.connected) { this.running = false; return Promise.resolve(); }
        const sc = this.scope;
        this.searchError = '';
        this.searchWarning = '';
        replaceAll(TRACE, []);
        return this.api.search({
            query: this.query, l2: [...sc.l2], land: sc.land, types: [...sc.types],
            sources: [...sc.src].filter(k => k !== CONNECTED_FOLDER_KEY)
        }).then(docs => {
            this.documents = docs;
            this.searchWarning = this.api.signavioError;
            this.resetDecisions();
            const res = this.api.lastSearch;
            if (res) {
                replaceAll(TRACE, (res.steps ?? []).map(s => ({
                    title: s.title, detail: s.detail, log: [], duration: Math.round((s.durationSec ?? 0) * 1000)
                })));
                this.missing = [...(res.missing ?? [])];
            }
            this.api.saveHistory({ query: this.query, scope: this.scopeJson(), n: docs.length, status: 'complete' })
                .then(() => this.loadHistory())
                .catch(err => console.error('Saving history failed', err));
        }).catch(err => {
            console.error('Document search failed', err);
            this.documents = [];
            this.resetDecisions();
            this.searchError = err?.error?.detail || err?.message || 'Search failed.';
            this.toast('Search failed: ' + this.searchError);
        }).finally(() => {
            this.running = false;
        });
    }

    /** Summary counters of the last API search (files scanned, candidates, …), or null. */
    public get searchSummary(): SearchSummary | null {
        return this.api.lastSearch?.summary ?? null;
    }

    private scopeJson(): any {
        const sc = this.scope;
        return { l2: [...sc.l2], land: sc.land, types: [...sc.types], sources: [...sc.src] };
    }

    /** Connect folder: reads the Azure Storage folder and uses its documents as the search source. */
    public async connectFolder(): Promise<void> {
        try {
            const docs = await this.azure.connect();
            this.documents = docs;
            this.scope.src.add(CONNECTED_FOLDER_KEY);
            this.resetDecisions();
            this.toast('Folder connected · ' + docs.length + ' documents read');
        } catch (e: any) {
            this.toast(e?.message || 'Could not connect');
        }
    }

    public disconnectFolder(): void {
        this.azure.disconnect();
        this.scope.src.delete(CONNECTED_FOLDER_KEY);
        this.documents = [];
        this.resetDecisions();
    }

    public startNew(): void {
        this.deck = { prompt: '', original: '', busy: false, result: null };
        this.published = null;
        this.pbl = null;
        this.pblKinds = {};
        this.missing = [];
        this.notApplicable.clear();
        this.selection.clear();
        this.resetDecisions();
        this.maxStep = 1;
        this.go(1);
    }

    //*************************************************************************
    //  Review
    //*************************************************************************
    public filtered(tab: 'all' | 'x' | DocType = this.tab): FoundDocument[] {
        const f = this.filters;
        let a = this.found();
        if (tab === 'x') {
            a = a.filter(d => this.agentExcluded(d));
        } else {
            a = a.filter(d => !this.agentExcluded(d));
            if (tab !== 'all') { a = a.filter(d => d.t === tab); }
        }
        if (f.conf !== 'all') { a = a.filter(d => confidenceClass(d.conf) === f.conf); }
        if (f.l2 !== 'all') { a = a.filter(d => d.l2.includes(f.l2)); }
        if (f.src !== 'all') { a = a.filter(d => d.src === f.src); }
        if (f.land !== 'all') { a = a.filter(d => this.coverageLandscape(d) === f.land); }
        if (f.q) {
            const q = f.q.toLowerCase();
            a = a.filter(d => (d.name + d.path + d.codes.join(' ')).toLowerCase().includes(q));
        }
        const sorters: Record<SortKey, (x: FoundDocument, y: FoundDocument) => number> = {
            conf: (x, y) => y.conf - x.conf,
            name: (x, y) => x.name.localeCompare(y.name),
            date: (x, y) => new Date(y.mod).getTime() - new Date(x.mod).getTime()
        };
        return [...a.filter(d => !this.done.has(d.id))].sort(sorters[this.sort]);
    }

    public clearFilters(): void {
        this.filters = this.defaultFilters();
    }

    public include(id: string): void {
        this.decisions[id] = 'inc';
        this.approved.add(id);
        delete this.reasons[id];
        this.done.add(id);
        this.syncDecision(id, 'inc');
    }

    public exclude(ids: string[], reason: string, why: string = ''): void {
        ids.forEach(id => {
            this.decisions[id] = 'exc';
            this.approved.delete(id);
            this.reasons[id] = reason;
            this.done.add(id);
            if (why) { this.whys[id] = why; }
            this.syncDecision(id, 'exc', reason, why);
        });
    }

    public includeAllOfType(type: DocType): void {
        this.found().filter(d => d.t === type && this.decisions[d.id] !== 'exc').forEach(d => {
            this.decisions[d.id] = 'inc';
            this.approved.add(d.id);
            this.done.add(d.id);
            this.syncDecision(d.id, 'inc');
        });
    }

    /** Saves a review decision on the server (PUT documents/{id}/decision) for API-sourced results. */
    private syncDecision(id: string, decision: Decision, reason: string = '', note: string = ''): void {
        if (this.documents.find(document => document.id === id)?.signavioFlowId) { return; }
        if (this.azure.connected || !this.api.enabled || !this.api.lastRunId) { return; }
        this.api.setDecision(id, { decision, reason, note }).catch(err => {
            console.error('Saving decision failed', err);
            this.toast('Could not save the decision on the server.');
        });
    }

    public toggleSelect(id: string, checked: boolean): void {
        checked ? this.selection.add(id) : this.selection.delete(id);
    }

    public toggleSelectGroup(type: DocType, checked: boolean): void {
        this.filtered().filter(d => d.t === type).forEach(d => this.toggleSelect(d.id, checked));
    }

    public includeSelected(): number {
        const n = this.selection.size;
        this.selection.forEach(id => this.include(id));
        this.selection.clear();
        return n;
    }

    //*************************************************************************
    //  Coverage
    //*************************************************************************
    public coverageLandscape(d: FoundDocument): FoundDocument['land'] {
        const land = documentLandscape(d);
        return d.t === 'kt' && land !== 'tobe' ? 'asis' : land;
    }

    public covered(t: DocType, land: Landscape, l2: string): boolean {
        return this.found().some(d => d.t === t && this.coverageLandscape(d) === land
            && (d.l2.includes(l2) || (d.t === 'signavio' && d.codes.includes(l2)))
            && this.decisions[d.id] === 'inc');
    }

    public isReportedMissing(key: string): boolean {
        return this.missing.some(m => m.key === key);
    }

    public isCellApplicable(t: InputDocType, land: Landscape): boolean {
        return !!DOC_TYPES[t][land] || this.found().some(d => d.t === t && this.coverageLandscape(d) === land);
    }

    public isCellInScope(land: Landscape): boolean {
        return this.scope.land === 'both' || this.scope.land === land;
    }

    public includedCount(t: DocType, land: Landscape): number {
        return this.found().filter(d => d.t === t && this.coverageLandscape(d) === land && this.decisions[d.id] === 'inc').length;
    }

    public approvedCount(t: DocType, land: FoundDocument['land']): number {
        return this.found().filter(d => d.t === t && this.coverageLandscape(d) === land
            && this.decisions[d.id] === 'inc' && this.approved.has(d.id)).length;
    }

    public isOpenGap(t: InputDocType, land: Landscape, l2: string): boolean {
        const k = gapKey(t, land, l2);
        return this.scope.types.has(t) && this.scope.l2.has(l2) && !this.covered(t, land, l2)
            && !this.notApplicable.has(k) && !this.isReportedMissing(k);
    }

    public gaps(): string[] {
        const out: string[] = [];
        INPUT_TYPES.forEach(t => LANDSCAPES.forEach(l => {
            if (this.isCellApplicable(t, l) && this.isCellInScope(l)) {
                L2_PROCESSES.forEach(x => {
                    if (this.isOpenGap(t, l, x.id)) { out.push(gapKey(t, l, x.id)); }
                });
            }
        }));
        return out;
    }

    public markNotApplicable(key: string): void {
        this.notApplicable.add(key);
    }

    //*************************************************************************
    //  Missing documents
    //*************************************************************************
    public saveMissing(doc: Omit<MissingDocument, 'key'>, index: number | null): void {
        const value: MissingDocument = { ...doc, key: gapKey(doc.type, doc.land, doc.l2) };
        if (index !== null && index >= 0 && index < this.missing.length) {
            this.missing[index] = value;
        } else {
            this.missing.push(value);
        }
    }

    public removeMissing(index: number): void {
        this.missing.splice(index, 1);
    }

    //*************************************************************************
    //  Publish / export
    //*************************************************************************
    public included(): FoundDocument[] {
        return this.found().filter(d => this.decisions[d.id] === 'inc');
    }

    public excluded(): FoundDocument[] {
        return this.found().filter(d => this.decisions[d.id] === 'exc' && d.agent !== 'exc');
    }

    public pending(): FoundDocument[] {
        return this.found().filter(d => this.decisions[d.id] === 'pend');
    }

    public reasonCounts(): { reason: string; n: number }[] {
        const rc: Record<string, number> = {};
        this.excluded().forEach(d => {
            const r = this.reasons[d.id] || 'No reason given';
            rc[r] = (rc[r] || 0) + 1;
        });
        return Object.entries(rc).map(([reason, n]) => ({ reason, n }));
    }

    /** Publishes the pack on the server (POST api/document-finder/packs) and sends the review feedback. */
    public async publish(name: string = '', note: string = ''): Promise<PublishedPack | null> {
        try {
            const pack = await this.api.publishPack({
                name: name || this.query,
                note,
                documentIds: this.included().map(d => d.id),
                excluded: this.excluded().map(d => ({ id: d.id, reason: this.reasons[d.id] || '', why: this.whys[d.id] })),
                missing: this.missing
            });
            if (!pack) { throw new Error('API is not configured.'); }
            this.published = pack;
            this.api.sendFeedback({
                packId: pack.id, decisions: { ...this.decisions }, reasons: { ...this.reasons }
            }).catch(err => console.error('Sending feedback failed', err));
            this.loadPacks();
            this.go(5);
            return pack;
        } catch (err: any) {
            console.error('Publish failed', err);
            this.toast('Publish failed: ' + (err?.error?.detail || err?.message || 'unknown error'));
            return null;
        }
    }

    /** Server-built pack ZIP for the published pack. */
    public downloadPublishedPack(): Promise<Blob | null> {
        return this.published ? this.api.downloadPack(this.published.id) : Promise.resolve(null);
    }

    public buildCsv(): string {
        const q = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
        const header = ['Document type', 'Landscape', 'Name', 'L2', 'Codes', 'Source', 'Path', 'Confidence', 'Last modified'];
        const rows = this.included().map(d => [
            DOC_TYPES[d.t].long, landscapeLabel(d.land, true), d.name, d.l2.join(' '), d.codes.join(' '),
            sourceName(d.src), d.path, d.conf, d.mod
        ]);
        return [header, ...rows].map(r => r.map(q).join(',')).join('\n');
    }

    public buildLinks(): string {
        return this.included().map(d => `${d.name} — ${d.path}`).join('\n');
    }

    //*************************************************************************
    //  Overlays / toast
    //*************************************************************************
    public openDrawer(id: string): void {
        if (this.missingUploadBusy) { return; }
        this.closeAll();
        this.drawerDocId = id;
    }

    public openExclude(ids: string[]): void {
        if (this.missingUploadBusy) { return; }
        this.closeAll();
        this.excludeIds = ids;
    }

    public openMissing(key: string | null = null, index: number | null = null): void {
        if (this.missingUploadBusy) { return; }
        this.closeAll();
        this.missingModal = { open: true, key, index };
    }

    public closeAll(): void {
        if (this.missingUploadBusy) { return; }
        this.drawerDocId = null;
        this.excludeIds = null;
        this.missingModal = { open: false, key: null, index: null };
    }

    public get overlayOpen(): boolean {
        return !!this.drawerDocId || !!this.excludeIds || this.missingModal.open;
    }

    public toast(message: string): void {
        this.toastMessage = message;
        this.toastVisible = true;
        clearTimeout(this.toastHandle);
        this.toastHandle = setTimeout(() => this.toastVisible = false, 2400);
    }

    public feedbackCount(): number {
        return Object.keys(this.reasons).length + this.missing.length;
    }
}
